import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import {
  CAPSULE_HEIGHT_CROUCH,
  CAPSULE_HEIGHT_STAND,
  CAPSULE_RADIUS,
  EYE_HEIGHT_CROUCH,
  EYE_HEIGHT_STAND,
  type MapDef,
} from "@rietz/shared";
import {
  CAPSULE_COLOR,
  CAPSULE_OPACITY,
  COLLISION_COLORS,
  COLLISION_EPS,
  COLLISION_OPACITY,
} from "./palette.js";

/**
 * F3 — what the server actually collides against: one edge box per `MapBox`,
 * tinted by material. Every box's `EdgesGeometry` is merged into ONE
 * `LineSegments` (873 draw calls would be a frame-time cliff), and boxes are
 * inflated by `COLLISION_EPS` so coincident lines do not z-fight the rendered
 * boxes.
 */

export interface BuiltWireframe {
  group: THREE.Group;
  /** Boxes actually emitted (degenerate ones are skipped). */
  boxCount: number;
  /** Line vertices in the merged geometry — 24 per box. */
  vertexCount: number;
  dispose(): void;
}

export function buildCollisionWireframe(map: MapDef): BuiltWireframe {
  const group = new THREE.Group();
  group.name = `debug:collision:${map.id}`;

  const parts: THREE.BufferGeometry[] = [];
  for (const solid of map.solids) {
    if (solid === undefined) continue;
    const geo = edgeBoxFor(solid);
    if (geo !== null) parts.push(geo);
  }

  if (parts.length === 0) {
    return { group, boxCount: 0, vertexCount: 0, dispose: () => {} };
  }

  const merged = mergeGeometries(parts, false);
  for (const p of parts) p.dispose();
  if (merged === null) {
    throw new Error("buildCollisionWireframe: geometry merge failed");
  }
  merged.computeBoundingSphere();

  const material = new THREE.LineBasicMaterial({
    vertexColors: true,
    transparent: true,
    opacity: COLLISION_OPACITY,
    depthWrite: false,
  });

  const lines = new THREE.LineSegments(merged, material);
  lines.name = "collision";
  // Spans the whole map and the player is always inside it: culling can only
  // ever cost time. Matches how the merged solids are treated.
  lines.frustumCulled = false;
  group.add(lines);

  const vertexCount = merged.attributes.position?.count ?? 0;

  return {
    group,
    boxCount: parts.length,
    vertexCount,
    dispose(): void {
      group.remove(lines);
      merged.dispose();
      material.dispose();
    },
  };
}

// ---------------------------------------------------------------------------

const edgeColor = new THREE.Color();

/** One inflated box outline in world space, coloured by material. */
function edgeBoxFor(solid: MapDef["solids"][number]): THREE.BufferGeometry | null {
  const { min, max } = solid.box;
  const sx = max.x - min.x;
  const sy = max.y - min.y;
  const sz = max.z - min.z;
  if (!(sx > 0) || !(sy > 0) || !(sz > 0)) return null;

  const box = new THREE.BoxGeometry(
    sx + COLLISION_EPS * 2,
    sy + COLLISION_EPS * 2,
    sz + COLLISION_EPS * 2,
  );
  const edges = new THREE.EdgesGeometry(box);
  box.dispose();

  edgeColor.setHex(COLLISION_COLORS[solid.material] ?? 0xffffff);

  const count = edges.attributes.position?.count ?? 0;
  const colors = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const o = i * 3;
    colors[o] = edgeColor.r;
    colors[o + 1] = edgeColor.g;
    colors[o + 2] = edgeColor.b;
  }
  edges.setAttribute("color", new THREE.BufferAttribute(colors, 3));

  edges.translate(
    (min.x + max.x) * 0.5,
    (min.y + max.y) * 0.5,
    (min.z + max.z) * 0.5,
  );
  return edges;
}

// ---------------------------------------------------------------------------
// The local player's capsule
// ---------------------------------------------------------------------------

/** Segments around the vertical axis for the equator rings. */
const RING_SEGMENTS = 20;
/** Segments per quarter arc on each hemisphere. */
const CAP_SEGMENTS = 6;
/** Vertical connectors between the two equators. */
const STRUTS = 4;

export interface CapsuleWireframe {
  group: THREE.Group;
  /** Current total height, metres. */
  readonly height: number;
  /** Moves the capsule so its base sits at (x, y, z). */
  setFeet(x: number, y: number, z: number): void;
  /** Restretches the cylinder section. Cheap: rewrites Y in place. */
  setHeight(h: number): void;
  dispose(): void;
}

/**
 * The player's own capsule as a wireframe — the one body never otherwise shown,
 * and the one whose step-ups you need to see. Pair with F6. Only the Y
 * coordinates of the upper half are rewritten on a stance change; no
 * allocation.
 */
export function buildCapsuleWireframe(
  radius = CAPSULE_RADIUS,
  height = CAPSULE_HEIGHT_STAND,
): CapsuleWireframe {
  const group = new THREE.Group();
  group.name = "debug:capsule";

  // Fixed vertex budget, so everything can go straight into typed arrays:
  // two equator rings, the struts, and four quarter-arcs per hemisphere.
  const n =
    RING_SEGMENTS * 4 + STRUTS * 2 + 2 /* planes */ * CAP_SEGMENTS * 2 /* signs */ * 4;

  const positions = new Float32Array(n * 3);
  const baseY = new Float32Array(n);
  const upper = new Uint8Array(n);
  let w = 0;

  /** Appends one vertex; `up` marks it as riding on top of the cylinder. */
  const push = (x: number, y: number, z: number, up: number): void => {
    positions[w * 3] = x;
    positions[w * 3 + 1] = y;
    positions[w * 3 + 2] = z;
    baseY[w] = y;
    upper[w] = up;
    w += 1;
  };

  // Two equator rings: the bottom at y = r, the top at y = 2r (i.e. r above
  // the bottom one once the zero-length cylinder is stretched).
  for (let i = 0; i < RING_SEGMENTS; i++) {
    const a0 = (i / RING_SEGMENTS) * Math.PI * 2;
    const a1 = ((i + 1) / RING_SEGMENTS) * Math.PI * 2;
    const x0 = Math.cos(a0) * radius;
    const z0 = Math.sin(a0) * radius;
    const x1 = Math.cos(a1) * radius;
    const z1 = Math.sin(a1) * radius;
    push(x0, radius, z0, 0);
    push(x1, radius, z1, 0);
    push(x0, radius, z0, 1);
    push(x1, radius, z1, 1);
  }

  // Vertical struts joining the rings — these are what read as "a capsule"
  // rather than "two floating circles".
  for (let i = 0; i < STRUTS; i++) {
    const a = (i / STRUTS) * Math.PI * 2;
    const x = Math.cos(a) * radius;
    const z = Math.sin(a) * radius;
    push(x, radius, z, 0);
    push(x, radius, z, 1);
  }

  // Hemispherical caps: two arcs each, in the XY and ZY planes.
  for (let plane = 0; plane < 2; plane++) {
    for (let i = 0; i < CAP_SEGMENTS; i++) {
      const t0 = (i / CAP_SEGMENTS) * (Math.PI * 0.5);
      const t1 = ((i + 1) / CAP_SEGMENTS) * (Math.PI * 0.5);
      // t = 0 at the equator, PI/2 at the pole.
      const r0 = Math.cos(t0) * radius;
      const r1 = Math.cos(t1) * radius;
      const y0 = radius - Math.sin(t0) * radius;
      const y1 = radius - Math.sin(t1) * radius;
      for (const sign of [1, -1]) {
        const ax0 = plane === 0 ? r0 * sign : 0;
        const az0 = plane === 0 ? 0 : r0 * sign;
        const ax1 = plane === 0 ? r1 * sign : 0;
        const az1 = plane === 0 ? 0 : r1 * sign;
        // Bottom cap, then its mirror image on top (baseY mirrored about r).
        push(ax0, y0, az0, 0);
        push(ax1, y1, az1, 0);
        push(ax0, 2 * radius - y0, az0, 1);
        push(ax1, 2 * radius - y1, az1, 1);
      }
    }
  }

  if (w !== n) {
    throw new Error(`buildCapsuleWireframe: wrote ${w} of ${n} vertices`);
  }

  const attr = new THREE.BufferAttribute(positions, 3);
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", attr);
  // Computed once so the geometry is well-formed; never refreshed, see below.
  geo.computeBoundingSphere();

  const material = new THREE.LineBasicMaterial({
    color: CAPSULE_COLOR,
    transparent: true,
    opacity: CAPSULE_OPACITY,
    depthWrite: false,
  });
  const lines = new THREE.LineSegments(geo, material);
  lines.name = "capsule";
  lines.frustumCulled = false;
  group.add(lines);

  let current = -1;

  const api: CapsuleWireframe = {
    group,
    get height(): number {
      return current;
    },
    setFeet(x: number, y: number, z: number): void {
      group.position.set(x, y, z);
    },
    setHeight(h: number): void {
      const clamped = h < radius * 2 ? radius * 2 : h;
      if (Math.abs(clamped - current) < 1e-4) return;
      current = clamped;
      const cyl = clamped - radius * 2;
      for (let i = 0; i < n; i++) {
        if (upper[i] === 1) positions[i * 3 + 1] = (baseY[i] ?? 0) + cyl;
      }
      attr.needsUpdate = true;
      // No bounding-sphere refresh: `frustumCulled` is off, so nothing reads
      // it, and a stance blend rewrites the height on every frame for ~100 ms.
    },
    dispose(): void {
      group.remove(lines);
      geo.dispose();
      material.dispose();
    },
  };

  api.setHeight(height);
  return api;
}

/**
 * Capsule height matching a RENDERED eye height, so the wireframe eases across
 * a stance change with the camera. Linear between the two authored pairs.
 */
export function capsuleHeightForEye(eyeHeight: number): number {
  const span = EYE_HEIGHT_STAND - EYE_HEIGHT_CROUCH;
  const t = span === 0 ? 1 : (eyeHeight - EYE_HEIGHT_CROUCH) / span;
  const clamped = t < 0 ? 0 : t > 1 ? 1 : t;
  return (
    CAPSULE_HEIGHT_CROUCH + (CAPSULE_HEIGHT_STAND - CAPSULE_HEIGHT_CROUCH) * clamped
  );
}
